/*
 * Copyright (c) 2019. ProVision Media Group Ltd. <https://provision.bg>
 * Venelin Iliev <http://veneliniliev.com>
 */

import { Component, OnInit, TemplateRef, ViewChild } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { finalize } from 'rxjs/operators';

import { AuthenticationService } from '@app/core';
import { AccessService } from '@app/core/access/access.service';
import {
  CheckInOutCorrectionPayload,
  CheckInOutService,
} from '@app/reports/check-in-out/check-in-out.service';
import { CheckInOut, CheckInOutInterval } from '@app/reports/check-in-out/check-in-out.model';
import { PaginatedResponse } from '@app/shared/models/paginated-response.model';
import { UserRoleType } from '@app/settings/users/user-role-type.enum';
import { User } from '@app/settings/users/user.model';

const moment = require('moment');

interface CheckInOutChartBar {
  label: string;
  date: string;
  dayName: string;
  hours: number;
  height: number;
  intervals: Array<string>;
  hasData: boolean;
  showLabel: boolean;
  record: CheckInOut | null;
  needsAttention: boolean;
}

@Component({
  selector: 'app-check-in-out',
  templateUrl: './check-in-out.component.html',
  styleUrls: ['./check-in-out.component.scss'],
})
export class CheckInOutComponent implements OnInit {
  // Temporary hard-coded access rule. Keep this list synchronized with the API.
  private static readonly TESS_EMAILS = [
    'martin.stefanov@esof.net',
    'tnolan@traveltofairs.ie',
  ];

  @ViewChild('dayDetailsModal') dayDetailsModal: TemplateRef<any>;

  readonly chartMaxHours = 12;
  readonly chartYAxisTicks = [12, 9, 6, 3, 0];

  form: FormGroup;
  correctionForm: FormGroup;
  records: Array<CheckInOut> = [];
  chartBars: Array<CheckInOutChartBar> = [];
  selectedDay: CheckInOutChartBar | null = null;
  editingIntervalIndex: number | null = null;
  loading = false;
  loaded = false;
  correctionSaving = false;
  totalMinutes = 0;

  en = {
    firstDayOfWeek: 1,
    dayNames: ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'],
    dayNamesShort: ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'],
    dayNamesMin: ['Su', 'Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa'],
    monthNames: [
      'January',
      'February',
      'March',
      'April',
      'May',
      'June',
      'July',
      'August',
      'September',
      'October',
      'November',
      'December',
    ],
    monthNamesShort: ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'],
    today: 'Today',
    clear: 'Clear',
    weekHeader: 'Wk',
  };

  private reportRequestId = 0;

  constructor(
    private formBuilder: FormBuilder,
    private checkInOutService: CheckInOutService,
    private accessService: AccessService,
    private authenticationService: AuthenticationService,
    private modalService: NgbModal
  ) {}

  get currentUser(): User | null {
    const profile = this.authenticationService.accessToken?.profile;
    return profile ? new User().deserialize(profile) : null;
  }

  get isAdmin(): boolean {
    return this.accessService.hasOneOfRoles([UserRoleType.ADMINISTRATOR]);
  }

  get isTess(): boolean {
    const userEmail = (this.currentUser?.email || '').trim().toLowerCase();

    return CheckInOutComponent.TESS_EMAILS.some(
      (email: string) => email.trim().toLowerCase() === userEmail
    );
  }

  get canViewAllUsers(): boolean {
    return this.isAdmin || this.isTess;
  }

  get canCorrect(): boolean {
    return this.isTess;
  }

  get hasSelectedUser(): boolean {
    return !!this.form?.get('user_id')?.value;
  }

  get chartMinWidth(): number {
    return Math.max(this.chartBars.length * 48, 500);
  }

  ngOnInit(): void {
    this.createForms();
    this.setDateRange(moment().startOf('isoWeek'), moment().endOf('isoWeek'));

    const user = this.currentUser;
    if (user) {
      this.form.patchValue({ user_id: user.id, user }, { emitEvent: false });
    }

    this.form.get('user_id').valueChanges.subscribe(() => this.loadReport());
    this.loadReport();
  }

  setCurrentWeek(): void {
    this.setDateRange(moment().startOf('isoWeek'), moment().endOf('isoWeek'));
    this.loadReport();
  }

  setCurrentMonth(): void {
    this.setDateRange(moment().startOf('month'), moment().endOf('month'));
    this.loadReport();
  }

  onDateChange(): void {
    this.loadReport();
  }

  loadReport(): void {
    if (!this.hasSelectedUser || this.form.invalid) {
      this.resetReport();
      return;
    }

    const startDate = moment(this.form.get('start_date').value);
    const endDate = moment(this.form.get('end_date').value);

    if (!startDate.isValid() || !endDate.isValid() || startDate.isAfter(endDate, 'day')) {
      this.resetReport();
      return;
    }

    const requestId = ++this.reportRequestId;
    this.loading = true;
    this.loaded = false;

    this.checkInOutService
      .index({
        user_id: Number(this.form.get('user_id').value),
        start_date: startDate.format('YYYY-MM-DD'),
        end_date: endDate.format('YYYY-MM-DD'),
      })
      .pipe(
        finalize(() => {
          if (requestId === this.reportRequestId) {
            this.loading = false;
          }
        })
      )
      .subscribe((response: PaginatedResponse) => {
        if (requestId !== this.reportRequestId) {
          return;
        }

        this.records = response.data as Array<CheckInOut>;
        this.totalMinutes = this.records.reduce(
          (sum: number, record: CheckInOut) => sum + this.getRecordMinutes(record),
          0
        );
        this.loaded = true;
        this.updateChartData();
      });
  }

  openDayDetails(bar: CheckInOutChartBar): void {
    if (!bar.record) {
      return;
    }

    this.selectedDay = bar;
    this.cancelIntervalEdit();
    this.modalService.open(this.dayDetailsModal, { size: 'xl', centered: true, scrollable: true });
  }

  startIntervalEdit(interval: CheckInOutInterval, index: number): void {
    if (!this.canCorrect || !this.selectedDay?.record || this.selectedDay.record.is_open) {
      return;
    }

    this.editingIntervalIndex = index;

    this.correctionForm.reset({
      check_in_at: this.getIntervalEditorDate(interval.check_in_at),
      check_out_at: this.getIntervalEditorDate(interval.check_out_at),
      edit_note: interval.edit_note || '',
    });
  }

  private getIntervalEditorDate(value: string | null): Date | null {
    const record = this.selectedDay?.record;

    if (!value || !record) {
      return null;
    }

    const workDate = this.getWorkDate(record);
    const intervalTime = moment(value, 'YYYY-MM-DD HH:mm:ss').format('HH:mm:ss');

    return moment(`${workDate} ${intervalTime}`, 'YYYY-MM-DD HH:mm:ss', true).toDate();
  }

  private getWorkDate(record: CheckInOut): string {
    const value: any = record.work_date;

    if (!value) {
      return '';
    }

    if (typeof value === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(value)) {
      return value;
    }

    const rawValue = value.date || value;
    return moment(rawValue).format('YYYY-MM-DD');
  }

  cancelIntervalEdit(): void {
    this.editingIntervalIndex = null;
    this.correctionForm.reset();
  }

  saveIntervalCorrection(): void {
    const record = this.selectedDay?.record;

    if (
      !this.canCorrect ||
      !record ||
      this.editingIntervalIndex === null ||
      this.correctionForm.invalid ||
      this.correctionSaving
    ) {
      this.correctionForm.markAllAsTouched();
      return;
    }

    const workDate = this.getWorkDate(record);
    const checkInTime = moment(this.correctionForm.get('check_in_at').value).format('HH:mm:ss');

    const checkOutTime = moment(this.correctionForm.get('check_out_at').value).format('HH:mm:ss');

    const payload: CheckInOutCorrectionPayload = {
      check_in_at: `${workDate} ${checkInTime}`,
      check_out_at: `${workDate} ${checkOutTime}`,
      edit_note: this.correctionForm.get('edit_note').value,
    };

    this.correctionSaving = true;
    this.checkInOutService
      .correctInterval(record.id, this.editingIntervalIndex, payload)
      .pipe(finalize(() => (this.correctionSaving = false)))
      .subscribe((updatedRecord: CheckInOut) => {
        const recordIndex = this.records.findIndex((item: CheckInOut) => item.id === updatedRecord.id);
        if (recordIndex >= 0) {
          this.records[recordIndex] = updatedRecord;
        }

        this.totalMinutes = this.records.reduce(
          (sum: number, item: CheckInOut) => sum + this.getRecordMinutes(item),
          0
        );
        this.updateChartData();
        this.selectedDay =
          this.chartBars.find((item: CheckInOutChartBar) => item.record?.id === updatedRecord.id) || null;
        this.cancelIntervalEdit();
      });
  }

  getRecordMinutes(record: CheckInOut): number {
    return record.is_open ? record.current_total_minutes : record.total_minutes;
  }

  formatMinutes(minutes: number): string {
    const safeMinutes = Number(minutes) || 0;
    const hours = Math.floor(safeMinutes / 60);
    const restMinutes = Math.round(safeMinutes % 60);
    return `${hours}h ${restMinutes}m`;
  }

  formatDateTime(value: any): string {
    if (!value) {
      return '—';
    }

    const parsed = moment(value.date || value);
    return parsed.isValid() ? parsed.format('DD.MM.YYYY HH:mm') : '—';
  }

  formatTime(value: any): string {
    if (!value) {
      return '—';
    }

    const parsed = moment(value.date || value);
    return parsed.isValid() ? parsed.format('HH:mm') : '—';
  }

  formatBarHours(hours: number): string {
    return `${hours.toFixed(2).replace(/\.00$/, '')}h`;
  }

  getBarHeight(hours: number): number {
    return !hours || hours <= 0 ? 0 : Math.min((hours / this.chartMaxHours) * 100, 100);
  }

  getBarIntervals(bar: CheckInOutChartBar): Array<string> {
    return bar.hasData ? bar.intervals : [];
  }

  private createForms(): void {
    this.form = this.formBuilder.group({
      user_id: [null, [Validators.required]],
      user: [null],
      start_date: [null, [Validators.required]],
      end_date: [null, [Validators.required]],
    });

    this.correctionForm = this.formBuilder.group({
      check_in_at: [null, [Validators.required]],
      check_out_at: [null, [Validators.required]],
      edit_note: ['', [Validators.required, Validators.maxLength(1000)]],
    });
  }

  private updateChartData(): void {
    const startDate = this.form.get('start_date').value;
    const endDate = this.form.get('end_date').value;

    if (!startDate || !endDate) {
      this.chartBars = [];
      return;
    }

    const recordsByDate = this.records.reduce((grouped: { [key: string]: CheckInOut }, record: CheckInOut) => {
      grouped[this.getWorkDate(record)] = record;
      return grouped;
    }, {});

    const cursor = moment(startDate).startOf('day');
    const end = moment(endDate).startOf('day');
    const totalDays = end.diff(cursor, 'days') + 1;
    const labelStep = totalDays <= 14 ? 1 : totalDays <= 31 ? 2 : Math.ceil(totalDays / 16);
    const bars: Array<CheckInOutChartBar> = [];
    let dayIndex = 0;

    while (cursor.isSameOrBefore(end)) {
      const key = cursor.format('YYYY-MM-DD');
      const record = recordsByDate[key] || null;
      const dayMinutes = record ? this.getRecordMinutes(record) : 0;
      const dayHours = Number((dayMinutes / 60).toFixed(2));
      const intervals = (record?.intervals || []).map((item: CheckInOutInterval) => {
        const start = this.formatTime(item.check_in_at);
        const finish = item.check_out_at ? this.formatTime(item.check_out_at) : 'Missing';
        return `${start} - ${finish}`;
      });

      bars.push({
        label: cursor.format('ddd DD.MM'),
        date: cursor.format('DD.MM.YYYY'),
        dayName: cursor.format('dddd'),
        hours: dayHours,
        height: dayHours > 0 ? Math.max((Math.min(dayHours, this.chartMaxHours) / this.chartMaxHours) * 100, 4) : 0,
        intervals,
        hasData: !!record,
        showLabel: dayIndex % labelStep === 0 || cursor.isSame(end, 'day'),
        record,
        needsAttention: !!record?.needs_attention,
      });

      cursor.add(1, 'day');
      dayIndex += 1;
    }

    this.chartBars = bars;
  }

  private setDateRange(startDate: any, endDate: any): void {
    this.form.patchValue({ start_date: startDate.toDate(), end_date: endDate.toDate() }, { emitEvent: false });
  }

  private resetReport(): void {
    this.reportRequestId += 1;
    this.records = [];
    this.chartBars = [];
    this.totalMinutes = 0;
    this.loaded = false;
    this.loading = false;
  }
}
